import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'components/ui';
import { Box, Typography, Checkbox, Avatar, Link } from '@mui/material';
import { OrderApproveIcon } from 'components/icons';
import { CloseIcon } from 'components/icons/CloseIcon';
import { useOrganizationsApi } from 'services/api/hooks/useOrganizationsApi.hook';
import { useSnackbar } from 'notistack';
import { Routes } from 'services/routes';
import { colorLightGrey, colorDarkGrey } from 'customColors';
import { useSWRConfig } from 'swr';
import { useOrganizationLeaderTerms } from 'utils/organization-leader-terms';

export interface OrganizationLeaderInvite {
  id: string;
  status: 'requested' | 'accepted' | 'declined';
  role: string;
  organization: {
    name: string;
    id: string;
    school: {
      name: string;
      iconUrl: string;
      termsUrl?: string;
      termsDisplayName: string;
    };
    logoUrl?: string;
  };
}

export interface PendingOrgLeaderInvitesModalProps {
  open: boolean;
  invites: OrganizationLeaderInvite[];
  onClose: () => void;
}

export const PendingOrgLeaderInvitesModal = ({
  open,
  invites,
  onClose,
}: PendingOrgLeaderInvitesModalProps) => {
  const organizationsApi = useOrganizationsApi();
  const { enqueueSnackbar } = useSnackbar();
  const { mutate } = useSWRConfig();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [processing, setProcessing] = useState<string | null>(null);

  const [inviteStatus, setInviteStatus] = useState<{
    [id: string]: 'Accepted' | 'Declined';
  }>({});

  const handleAction = async (
    invite: OrganizationLeaderInvite,
    action: 'accept' | 'decline',
  ) => {
    if (!acceptedTerms) {
      enqueueSnackbar('Please accept the terms first', { variant: 'error' });
      return;
    }

    setProcessing(invite.id);
    try {
      if (action === 'accept') {
        const result = await organizationsApi.acceptLeaderInvite(
          invite.id,
          true,
        );
        if (!result.success)
          throw new Error(result.error.message || 'Failed to accept invite');
        setInviteStatus(prev => ({ ...prev, [invite.id]: 'Accepted' }));
        enqueueSnackbar('Invite accepted successfully', { variant: 'success' });
        mutate('all-org-leader-invites');
      } else {
        const result = await organizationsApi.declineLeaderInvite(invite.id);
        if (!result.success)
          throw new Error(result.error.message || 'Failed to decline invite');

        setInviteStatus(prev => ({ ...prev, [invite.id]: 'Declined' }));
        enqueueSnackbar('Invite declined successfully', { variant: 'success' });
        mutate('all-org-leader-invites');
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to process invite';
      enqueueSnackbar(message, {
        variant: 'error',
      });
    } finally {
      setProcessing(null);
    }
  };

  const pendingInvites = invites.filter(
    invite => invite.status === 'requested',
  );
  const school = pendingInvites[0]?.organization?.school;
  const schoolIcon = school?.iconUrl;
  const { termsUrl, termsDisplayText, checkboxText } = useOrganizationLeaderTerms({
    termsUrl: school?.termsUrl,
    termsDisplayName: school?.termsDisplayName,
  });

  // Close modal if no invites (useEffect to avoid setState during render)
  useEffect(() => {
    if (!invites.length && open)
      onClose();
  }, [invites.length, open, onClose]);

  if (!invites.length)
    return null;

  return (
    <Modal open={open} showCloseIcon={false} onClose={onClose}>
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={24}
          mt={16}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              fontSize: '20px',
              color: '#000',
              flex: 1,
              textAlign: { xs: 'left', md: 'center' },
            }}
          >
            You have been invited to be an organization leader.
          </Typography>
          <Box
            onClick={onClose}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              ml: 24,
              fontSize: '20px',
            }}
          >
            <CloseIcon />
          </Box>
        </Box>
        <Box
          sx={{
            border: `1px solid ${colorLightGrey}`,
            borderRadius: 1,
            padding: { xs: 12, sm: 16 },
            mb: { xs: 16, sm: 24 },
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            mb={6}
          >
            <Link
              href={termsUrl}
              target="_blank"
              rel="noopener"
              underline="always"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: 'inherit',
              }}
            >
              <OrderApproveIcon fontSize="medium" />
              <Typography
                align="left"
                variant="caption"
                sx={{ fontSize: '1.2rem' }}
              >
                {termsDisplayText}
              </Typography>
            </Link>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            sx={{ fontSize: '0.8rem', gap: 8 }}
          >
            <Checkbox
              sx={{
                padding: 0,
                margin: 0,
                '& .MuiCheckbox-root': {},
              }}
              checked={acceptedTerms}
              onChange={e => setAcceptedTerms(e.target.checked)}
            />
            <Typography sx={{ fontWeight: 500 }}>{checkboxText}</Typography>
          </Box>
        </Box>

        {invites.map(invite => {
          const status = inviteStatus[invite.id];
          const isPending = invite.status === 'requested' && !status;
          const orgIcon = invite.organization?.logoUrl || schoolIcon;
          return (
            <Box
              key={invite.id}
              sx={{
                pb: { xs: 12, sm: 8 },
                mb: 2,
                borderRadius: 1,
                backgroundColor: 'background.paper',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 2, sm: 2 },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  pt: { xs: 12, sm: 8 },
                  gap: 2,
                  flex: { sm: 1 },
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                <Avatar
                  src={orgIcon}
                  alt={invite.organization?.name}
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: 'background.paper',
                  }}
                />
                <Box flex={1}>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: '1rem', fontWeight: 500 }}
                  >
                    {invite.organization?.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: '0.9rem', fontWeight: 400 }}
                  >
                    Role: {invite.role}
                  </Typography>
                </Box>
              </Box>
              {isPending ? (
                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: 16, sm: 16 },
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                    width: { xs: '100%', sm: 'auto' },
                    mt: { xs: 8, sm: 0 },
                  }}
                >
                  <Button
                    variant="paper"
                    disabled={!acceptedTerms || processing === invite.id}
                    onClick={() => handleAction(invite, 'decline')}
                    sx={{
                      flex: { xs: 1, sm: 'none' },
                      minWidth: { xs: 0, sm: '100px' },
                      height: '36px',
                      border: '1px solid',
                      borderColor: 'error.main',
                      backgroundColor: 'background.paper',
                      color: 'error.main',
                      '&:disabled': {
                        border: '1px solid',
                        borderColor: 'grey.300',
                        backgroundColor: 'grey.100',
                        color: 'text.disabled',
                      },
                    }}
                  >
                    Decline
                  </Button>
                  <Button
                    variant="vibrant"
                    disabled={!acceptedTerms || processing === invite.id}
                    onClick={() => handleAction(invite, 'accept')}
                    sx={{
                      flex: { xs: 1, sm: 'none' },
                      minWidth: { xs: 0, sm: '100px' },
                      height: '36px',
                    }}
                  >
                    Accept
                  </Button>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: { xs: 'flex', sm: 'contents' },
                    alignItems: 'center',
                    gap: 2,
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <Box
                    sx={{ display: { xs: 'block', sm: 'none' }, width: 48 }}
                  />{' '}
                  <Typography
                    variant="body2"
                    color={
                      status === 'Accepted' ? 'success.main' : 'error.main'
                    }
                    sx={{
                      minWidth: '100px',
                      textAlign: { xs: 'left', sm: 'center' },
                      fontWeight: 400,
                      fontSize: '14px',
                      backgroundColor:
                        status === 'Accepted'
                          ? 'rgba(92, 218, 100, 0.10)'
                          : 'rgba(155, 0, 0, 0.05)',
                      borderRadius: 1,
                      padding: '4px 8px',
                    }}
                  >
                    {status ||
                      (invite.status === 'accepted' ? 'Accepted' : 'Declined')}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
      {(() => {
        const actionedIds = Object.keys(inviteStatus);
        if (actionedIds.length === 0) return null;
        const anyAccepted = invites.some(
          invite =>
            (inviteStatus[invite.id] === 'Accepted' ||
              invite.status === 'accepted') &&
            actionedIds.includes(invite.id),
        );
        return (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            mt={24}
            gap={16}
          >
            {anyAccepted && (
              <Button
                sx={{
                  width: { xs: '100%', sm: '320px' },
                  height: '47px',
                  minWidth: { xs: '200px', sm: '320px' },
                }}
                variant="vibrant"
                onClick={() => {
                  const acceptedInvite = invites.find(
                    invite =>
                      (inviteStatus[invite.id] === 'Accepted' ||
                        invite.status === 'accepted') &&
                      actionedIds.includes(invite.id),
                  );
                  const orgId = acceptedInvite?.organization?.id;
                  if (orgId) {
                    window.location.href = Routes.admin.organizationDetail(
                      orgId,
                    );
                  }
                }}
              >
                Organization Dashboard
              </Button>
            )}
            <Typography
              onClick={onClose}
              sx={{
                cursor: 'pointer',
                textDecoration: 'underline',
                color: colorDarkGrey,
                fontWeight: 400,
                fontSize: '0.9rem',
                textAlign: 'center',
              }}
            >
              Close
            </Typography>
          </Box>
        );
      })()}

      {Object.keys(inviteStatus).length === 0 && (
        <Box display="flex" justifyContent="center" mt={24}>
          <Typography
            onClick={onClose}
            sx={{
              cursor: 'pointer',
              textDecoration: 'underline',
              color: colorDarkGrey,
              fontWeight: 400,
              fontSize: '0.9rem',
              textAlign: 'center',
            }}
          >
            Close
          </Typography>
        </Box>
      )}
    </Modal>
  );
};
